import { CORE_PHASE_COLORS, type CorePhase, type PipelineStage } from '@/types/pipeline';

export const CREATE_SAFE_INITIAL_PIPELINE_PHASES = new Set<CorePhase>(['DRAFT', 'SCHEDULED']);

export const TERMINAL_PIPELINE_PHASES = new Set<CorePhase>(['CLOSED', 'CANCELLED', 'ARCHIVED']);

export const DETOUR_PIPELINE_PHASES = new Set<CorePhase>(['ON_HOLD', 'CANCELLED', 'ARCHIVED']);

export interface PipelineWorkflowTransitionInput {
  stages: PipelineStage[];
  currentStageId?: string | null;
  targetStageId?: string | null;
  pausedFromStageId?: string | null;
}

export function sortPipelineStages(stages: PipelineStage[] = []): PipelineStage[] {
  return stages.slice().sort((a, b) => a.sortOrder - b.sortOrder);
}

export function normalizePipelinePhase(value: string | null | undefined): CorePhase | null {
  const normalized = String(value ?? '').toUpperCase() as CorePhase;
  return normalized in CORE_PHASE_COLORS ? normalized : null;
}

export function findCurrentPipelineStage(
  stages: PipelineStage[],
  currentPhase: CorePhase | null,
  status: string | null | undefined,
  stageId?: string | null,
): PipelineStage | null {
  const byId = stageId ? stages.find((stage) => stage.id === stageId) : null;
  if (byId) return byId;

  const normalizedStatus = normalizeStageName(status);
  const byName = normalizedStatus
    ? stages.find((stage) => normalizeStageName(stage.name) === normalizedStatus)
    : null;

  if (byName) return byName;
  return stages.find((stage) => stage.corePhase === currentPhase) ?? null;
}

export function isTerminalPipelineStage(stage: PipelineStage | null | undefined): boolean {
  return Boolean(stage && (stage.isTerminal || TERMINAL_PIPELINE_PHASES.has(stage.corePhase)));
}

export function isDetourPipelineStage(stage: PipelineStage | null | undefined): boolean {
  return Boolean(stage && DETOUR_PIPELINE_PHASES.has(stage.corePhase));
}

export function getPipelinePathStages(stages: PipelineStage[]): PipelineStage[] {
  return stages.filter((stage) => !isDetourPipelineStage(stage));
}

/**
 * The Hold / Cancel detours a job can take from its current stage. `phases` is
 * what the job may detour into right now (none once it is terminal).
 *
 * A tenant's pipeline can leave out a Cancelled stage, and the job must still be
 * cancellable: `cancelWithoutStage` asks for a cancel that moves it to no stage.
 * It waits for the stages to load, since an empty list while loading would
 * otherwise offer it for every pipeline.
 */
export function getJobDetourOptions({
  stages,
  currentStageId,
  phases,
  isLoading,
}: {
  stages: PipelineStage[];
  currentStageId: string | null | undefined;
  phases: ReadonlySet<CorePhase>;
  isLoading: boolean;
}): { stages: PipelineStage[]; cancelWithoutStage: boolean } {
  return {
    stages: stages.filter((stage) => phases.has(stage.corePhase) && stage.id !== currentStageId),
    cancelWithoutStage:
      phases.has('CANCELLED') &&
      !isLoading &&
      !stages.some((stage) => stage.corePhase === 'CANCELLED'),
  };
}

export function getNearestPathStageBefore(
  stages: PipelineStage[],
  currentStage: PipelineStage | null | undefined,
): PipelineStage | null {
  const pathStages = getPipelinePathStages(stages);
  if (!currentStage) return pathStages[0] ?? null;
  const currentIndex = stages.findIndex((stage) => stage.id === currentStage.id);
  if (currentIndex < 0) return pathStages[0] ?? null;
  return (
    stages
      .slice(0, currentIndex)
      .filter((stage) => !isDetourPipelineStage(stage))
      .at(-1) ?? null
  );
}

export function getNextPipelinePathStage(
  stages: PipelineStage[],
  currentStage: PipelineStage | null | undefined,
): PipelineStage | null {
  const pathStages = getPipelinePathStages(stages);
  if (!currentStage || isTerminalPipelineStage(currentStage)) return null;
  const effectiveCurrentStage = isDetourPipelineStage(currentStage)
    ? getNearestPathStageBefore(stages, currentStage)
    : currentStage;
  const currentIndex = effectiveCurrentStage
    ? pathStages.findIndex((stage) => stage.id === effectiveCurrentStage.id)
    : -1;
  if (currentIndex < 0) return null;
  return pathStages[currentIndex + 1] ?? null;
}

export function isPipelineWorkflowTransitionAllowed({
  stages,
  currentStageId,
  targetStageId,
  pausedFromStageId,
}: PipelineWorkflowTransitionInput): boolean {
  if (!targetStageId) return false;
  if (!currentStageId || currentStageId === targetStageId) return true;

  const sortedStages = sortPipelineStages(stages);
  const targetStage = sortedStages.find((stage) => stage.id === targetStageId);
  if (!targetStage) return false;
  if (isDetourPipelineStage(targetStage)) return true;

  const currentStage = sortedStages.find((stage) => stage.id === currentStageId);
  if (!currentStage) return false;

  if (isDetourPipelineStage(currentStage)) {
    return Boolean(pausedFromStageId && pausedFromStageId === targetStageId);
  }

  const pathStages = getPipelinePathStages(sortedStages);
  const currentIndex = pathStages.findIndex((stage) => stage.id === currentStageId);
  if (currentIndex < 0) return false;

  return pathStages[currentIndex + 1]?.id === targetStageId;
}

export function requiresPipelineWorkflowOverride(input: PipelineWorkflowTransitionInput): boolean {
  return !isPipelineWorkflowTransitionAllowed(input);
}

/** `IN_PROGRESS` → "In Progress". The one spelling of a core phase for readers. */
export function formatCorePhase(value: CorePhase): string {
  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * The core job status a pipeline stage moves the job into — but only when the
 * stage's own name doesn't already say it.
 *
 * A tenant renames stages freely ("Rough-In", "Awaiting Parts"), and every
 * job-facing surface shows that custom name alone. Nothing on the job then
 * connects "Rough-In" to the IN_PROGRESS status that filters, reports and
 * automations actually key on — which is what makes a job's stage and a visit's
 * status read as two unexplained states side by side.
 *
 * Returns null for a stage that is already named after its phase ("Scheduled"),
 * so the caller renders one label rather than the same word twice. The
 * comparison ignores case, underscores and surrounding space, the same
 * normalisation `findCurrentPipelineStage` matches stage names by.
 */
export function stageCoreStatusLabel(stage: {
  name?: string | null;
  corePhase?: string | null;
}): string | null {
  const phase = normalizePipelinePhase(stage.corePhase);
  if (!phase) return null;
  const label = formatCorePhase(phase);
  if (normalizeStageName(stage.name) === normalizeStageName(label)) return null;
  return label;
}

export function isSelectableInitialStage(stage: {
  isTerminal?: boolean;
  corePhase?: string;
}): boolean {
  const phase = normalizePipelinePhase(stage.corePhase);
  return Boolean(phase && !stage.isTerminal && CREATE_SAFE_INITIAL_PIPELINE_PHASES.has(phase));
}

function normalizeStageName(value: string | null | undefined): string {
  return String(value ?? '')
    .trim()
    .replace(/_/g, ' ')
    .toLowerCase();
}
